import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { getReferencesOptions } from '../../facebook';
import { observer } from 'mobx-react-lite';
import useRulePageMode from '../../hooks/useRulePageMode';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import { useCurrentNetwork } from '../../../../hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { LevelValue, References } from '../../types';
import type { Option, OptionValue } from '../../../../types/shared';
import { RuleEditAutoCompleteStyled } from '../RuleEditComponents';
import type { ConditionVariantProps } from '../../types';
import { useFormContext } from 'react-hook-form';
import type { BasicFormValues } from '../../facebook';
import { checkMetricWithLevel } from '../../utils';

export const ConditionMetrics: FC<ConditionVariantProps> = observer(
  ({ indexCondition, indexTask, field, indexVariant }) => {
    const { isRead } = useRulePageMode();
    const isVariant = typeof indexVariant === 'number';
    const disabled = isRead || isVariant;
    const tasksFormStore = useCurrentTasksFormStore();
    const network = useCurrentNetwork();

    const { data: metrics } = useSuspenseQuery({
      ...getReferencesOptions(network),
      select: (data: References): Option[] =>
        data.conditions.metrics.map(({ name, id }) => ({ value: id, label: name })),
    });

    //only for FB
    const { watch } = useFormContext<BasicFormValues>();
    const ruleLevel = watch('level') as LevelValue;

    const filteredMetrics = useMemo(() => {
      return metrics.filter((m) => {
        if (network === 'facebook') {
          return checkMetricWithLevel({ value: m.value as string, level: ruleLevel });
        }
        return true;
      });
    }, [ruleLevel, metrics, network]);

    const handleChangeMetric = useCallback(
      (value: OptionValue) => {
        tasksFormStore.setConditionMetric(indexTask, indexCondition, field, value as string);
      },
      [indexTask, indexCondition, field, tasksFormStore]
    );

    const value = isVariant
      ? tasksFormStore.getVariantOnTaskCondition(indexTask, indexVariant, indexCondition)?.[field]
      : tasksFormStore.getConditionMetric(indexTask, indexCondition)?.[field];

    return (
      <RuleEditAutoCompleteStyled
        allSelectable={false}
        value={value}
        onChange={handleChangeMetric}
        id={`Autocomplete-${field}-${indexTask}-${indexCondition}`}
        options={filteredMetrics ?? []}
        InputProps={{
          ...(tasksFormStore?.propsRestriction ?? null),
          disabled: disabled,
        }}
      />
    );
  }
);

export default ConditionMetrics;
